import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tab,
  Tabs,
  Box,
} from "@mui/material";
import { Edit, Package, Plus } from "lucide-react";
import { infoToast } from "../../../../../services/toast.service";

const ProductPackConfigurations = ({ productData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [expandedPack, setExpandedPack] = useState(null);

  // Generate pack types based on product data
  const generatePackTypes = () => {
    // Default empty pack types structure
    const defaultPackTypes = [
      {
        type: "SECONDARY",
        label: "Secondary Packs",
        packs: [],
      },
      {
        type: "TERTIARY",
        label: "Tertiary Packs",
        packs: [],
      },
      {
        type: "CUSTOM",
        label: "Custom Packs",
        packs: [],
      },
    ];

    // If no product data or no sellAsOptions, return the default empty structure
    if (!productData || !productData.sellAsOptions) {
      return defaultPackTypes;
    }

    const { sellAsOptions } = productData;
    const packTypes = [...defaultPackTypes];

    // Process selected configurations from sellAsOptions
    if (sellAsOptions.selectedConfigurations && sellAsOptions.configurations) {
      sellAsOptions.selectedConfigurations.forEach(configType => {
        const config = sellAsOptions.configurations[configType];

        // Skip primary configuration as it's not a pack
        if (configType === "primary") {
          return;
        }

        // Determine which pack type to add to
        const packTypeIndex =
          configType === "secondary" ? 0 : configType === "tertiary" ? 1 : 2;

        // Create pack object
        const pack = {
          id: `${configType}-${config.name}`,
          name:
            config.name ||
            `${configType.charAt(0).toUpperCase() + configType.slice(1)} Pack`,
          description: config.description || `Standard ${configType} pack`,
          totalItems: config.items || 0,
          totalValue: config.price || 0,
          items: [],
        };

        // Add mock items based on configuration
        if (config.items && config.price) {
          const pricePerUnit = Math.round(config.price / config.items);

          // Default items if real item data isn't available
          if (configType === "secondary") {
            // For secondary packs, create items based on product sizes if available
            let sizes = [];
            if (productData.size && typeof productData.size === "string") {
              sizes = productData.size.split(",").map(s => s.trim());
            }

            if (sizes.length > 0) {
              // Distribute items across sizes
              const itemsPerSize = Math.max(
                1,
                Math.floor(config.items / sizes.length),
              );
              let remainingItems = config.items;

              sizes.forEach(size => {
                const quantity = Math.min(itemsPerSize, remainingItems);
                if (quantity > 0) {
                  pack.items.push({
                    size: size,
                    color: productData.color || "Default",
                    quantity: quantity,
                    price: pricePerUnit,
                  });
                  remainingItems -= quantity;
                }
              });

              // Assign any remaining items to the first size
              if (remainingItems > 0 && sizes.length > 0) {
                pack.items[0].quantity += remainingItems;
              }
            } else {
              // If no sizes, create a single item entry
              pack.items.push({
                size: productData.size || "Standard",
                color: productData.color || "Default",
                quantity: config.items,
                price: pricePerUnit,
              });
            }
          } else if (configType === "tertiary") {
            // For tertiary packs, reference secondary packs if available
            const secondaryConfig =
              sellAsOptions.selectedConfigurations.includes("secondary")
                ? sellAsOptions.configurations.secondary
                : null;

            if (secondaryConfig) {
              // Create an item referencing the secondary pack
              pack.items.push({
                size: secondaryConfig.name || "Standard Pack",
                color: productData.color || "Default",
                quantity: Math.ceil(
                  config.items / (secondaryConfig.items || 1),
                ),
                price: secondaryConfig.price || pricePerUnit,
              });
            } else {
              // If no secondary pack reference, use standard format
              pack.items.push({
                size: "Bulk Pack",
                color: productData.color || "Default",
                quantity: config.items,
                price: pricePerUnit,
              });
            }
          }
        } else {
          // If no detailed configuration, add a default item
          pack.items.push({
            size: productData.size || "Standard",
            color: productData.color || "Default",
            quantity: config.items || 1,
            price: productData.sellingPrice || 0,
          });
        }

        // Add the pack to the appropriate pack type
        packTypes[packTypeIndex].packs.push(pack);
      });
    }

    return packTypes;
  };

  // Generate pack types based on product data
  const packTypes = generatePackTypes();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleToggleExpand = packId => {
    setExpandedPack(expandedPack === packId ? null : packId);
  };

  const renderPackCard = (pack, type) => (
    <div
      key={pack.id}
      className="border border-gray-200 rounded-md overflow-hidden shadow-sm mb-5 bg-white">
      {/* Pack Header */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-medium text-gray-800">{pack.name}</h3>
            <p className="text-sm text-gray-500 mt-1">{pack.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <Chip
              label={type}
              size="small"
              sx={{
                backgroundColor:
                  type === "SECONDARY"
                    ? "#F9F8FE"
                    : type === "TERTIARY"
                    ? "#FDF4FF"
                    : "#F0FDF4",
                color:
                  type === "SECONDARY"
                    ? "#5652B0"
                    : type === "TERTIARY"
                    ? "#C026D3"
                    : "#16A34A",
                fontFamily: "Outfit",
                fontWeight: 500,
                fontSize: "0.75rem",
                border:
                  type === "SECONDARY"
                    ? "1px solid #EFEDFB"
                    : type === "TERTIARY"
                    ? "1px solid #FAD5F5"
                    : "1px solid #DCFCE7",
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
            <button
              onClick={() => handleToggleExpand(pack.id)}
              className="p-1 hover:bg-gray-100 rounded-full">
              {expandedPack === pack.id ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5652B0"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="m18 15-6-6-6 6" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5652B0"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Pack Summary (always visible) */}
      <div className="p-4 flex justify-between items-center border-b border-gray-100">
        <div className="flex items-center gap-2 text-gray-700">
          <Package size={16} />
          <span className="text-sm font-medium">
            Total Items: {pack.totalItems}
          </span>
        </div>
        <div className="text-right">
          <span className="text-sm text-gray-700">Pack Value:</span>
          <span className="ml-2 font-medium text-[#5652B0]">
            ₹{pack.totalValue.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Pack Details (expandable) */}
      {expandedPack === pack.id && (
        <div className="p-4 bg-[#F9FAFB]">
          <TableContainer className="border border-gray-200 rounded-md bg-white shadow-sm">
            <Table size="small">
              <TableHead className="bg-[#F9F8FE]">
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#4B5563",
                    }}>
                    Size
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#4B5563",
                    }}>
                    Color
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#4B5563",
                    }}>
                    Quantity
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#4B5563",
                    }}>
                    Price (₹)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#4B5563",
                    }}>
                    Total (₹)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pack.items.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": {
                        borderBottom: 0,
                      },
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}>
                    <TableCell
                      sx={{
                        fontFamily: "Outfit",
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                        fontSize: "0.875rem",
                      }}>
                      {item.size}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Outfit",
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                        fontSize: "0.875rem",
                      }}>
                      {item.color}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontFamily: "Outfit",
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                        fontSize: "0.875rem",
                      }}>
                      {item.quantity}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: "Outfit",
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                        fontSize: "0.875rem",
                      }}>
                      {item.price.toLocaleString()}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: "Outfit",
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                        fontSize: "0.875rem",
                      }}>
                      {(item.quantity * item.price).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-md p-5 border border-gray-200 shadow-sm">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-semibold text-gray-800">
          Pack Configurations
        </h2>
        <button
          className="px-3 py-1.5 text-[#5652B0] hover:bg-[#F9F8FE] border border-[#5652B0] rounded-md flex items-center gap-1.5"
          onClick={() => infoToast("Edit not available in demo.")}>
          <Edit size={15} />
          <span className="text-sm font-medium">Edit</span>
        </button>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "0.875rem",
              fontFamily: "Outfit",
              fontWeight: 500,
            },
            "& .Mui-selected": {
              color: "#5652B0",
              fontWeight: 600,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#5652B0",
            },
          }}>
          <Tab label="All Packs" />
          {packTypes.map((type, index) => (
            <Tab key={index} label={type.label} />
          ))}
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        // All packs
        <div>
          {packTypes.map(packType => (
            <React.Fragment key={packType.type}>
              {packType.packs.length > 0 && (
                <>
                  <div className="flex items-center mb-3 mt-5">
                    <h3 className="text-gray-800 font-medium">
                      {packType.label}
                    </h3>
                    <div className="flex-1 h-px bg-gray-200 ml-3"></div>
                  </div>
                  {packType.packs.map(pack =>
                    renderPackCard(pack, packType.type),
                  )}
                </>
              )}
            </React.Fragment>
          ))}

          {packTypes.every(type => type.packs.length === 0) && (
            <div className="text-center py-10 bg-gray-50 border border-gray-200 rounded-md">
              <Package size={36} className="mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">No pack configurations found</p>
              <p className="text-sm text-gray-400 mt-2">
                {productData && productData.name
                  ? `${productData.name} doesn't have any pack configurations.`
                  : "This product does not have any pack configurations."}
              </p>
            </div>
          )}
        </div>
      ) : (
        // Specific pack type
        <div>
          {packTypes[activeTab - 1].packs.length > 0 ? (
            packTypes[activeTab - 1].packs.map(pack =>
              renderPackCard(pack, packTypes[activeTab - 1].type),
            )
          ) : (
            <div className="text-center py-10 bg-gray-50 border border-gray-200 rounded-md">
              <Package size={36} className="mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">
                No {packTypes[activeTab - 1].label.toLowerCase()} found
              </p>
              <button className="mt-4 flex items-center gap-1.5 px-4 py-2 bg-[#F9F8FE] text-[#5652B0] border border-[#EFEDFB] rounded-md mx-auto font-medium text-sm">
                <Plus size={16} />
                Add {packTypes[activeTab - 1].type.toLowerCase()} pack
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductPackConfigurations;
